import React, { useState, useEffect } from "react";
import NetwalaModal from "../Items/Modal/Index";
import { Row, Col, Typography } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import "./styles/RequestIPTVModal.css";
import "./styles/changeUserStatusModal.css";
import NetwalaButton from "../Items/Button/Index";
import { ButtonProps } from "../../helpers/constants";
import LocationIcon from "./../../assets/svgs/ProfileIcons/Location.svg";
import UserProfileBlue from "./../../assets/svgs/UserProfileIcons/UserProfileBlue.svg";

interface IUpdateStaffAreaLimitTypeModalProps {
  show: boolean;
  handleClose: (
    isChange: boolean,
    newType?: "AREA_WISE" | "COMPANY_WISE"
  ) => void;
  currentType?: "AREA_WISE" | "COMPANY_WISE";
}

const UpdateStaffAreaLimitTypeModal: React.FC<
  IUpdateStaffAreaLimitTypeModalProps
> = ({ show, handleClose, currentType }) => {
  const [selectedType, setSelectedType] = useState<
    "AREA_WISE" | "COMPANY_WISE"
  >(currentType || "COMPANY_WISE");

  useEffect(() => {
    if (show && currentType) {
      setSelectedType(currentType);
    }
  }, [show, currentType]);

  const optionCards = [
    {
      value: "COMPANY_WISE" as const,
      title: "Company Wise",
      description: "View all connections across the entire company",
      icon: UserProfileBlue,
      details: [
        "Access to all company connections",
        "No area restrictions",
        "Full company-wide visibility",
      ],
    },
    {
      value: "AREA_WISE" as const,
      title: "Area Wise",
      description: "View only connections from assigned areas",
      icon: LocationIcon,
      details: [
        "Limited to assigned areas only",
        "Child areas included automatically",
        "Restricted access for better control",
      ],
    },
  ];

  return (
    <>
      <NetwalaModal
        title="Update Connection View Type"
        headerStyle={{
          backgroundColor: "#292D8E",
          color: "#FFFFFF",
          fontSize: "18px",
          fontWeight: "500",
          height: "50px",
        }}
        open={show}
        onCancel={() => {
          handleClose(false);
        }}
        width={700}
      >
        <div style={{ padding: "20px 24px" }}>
          <Typography
            style={{
              fontSize: "14px",
              color: "#666",
              marginBottom: "30px",
              textAlign: "center",
            }}
          >
            Choose how this staff member views connections. This setting
            determines their access scope.
          </Typography>

          <Row gutter={[20, 20]} style={{ marginBottom: "30px" }}>
            {optionCards.map((option) => {
              const isSelected = selectedType === option.value;
              return (
                <Col span={12} key={option.value}>
                  <div
                    onClick={() => setSelectedType(option.value)}
                    style={{
                      border: `2px solid ${isSelected ? "#292D8E" : "#E0E0E0"}`,
                      borderRadius: "12px",
                      padding: "24px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      backgroundColor: isSelected ? "#F5F7FF" : "#FFFFFF",
                      position: "relative",
                      height: "100%",
                      boxShadow: isSelected
                        ? "0 4px 12px rgba(41, 45, 142, 0.15)"
                        : "0 2px 8px rgba(0, 0, 0, 0.05)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#292D8E";
                        e.currentTarget.style.boxShadow =
                          "0 4px 12px rgba(41, 45, 142, 0.1)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = "#E0E0E0";
                        e.currentTarget.style.boxShadow =
                          "0 2px 8px rgba(0, 0, 0, 0.05)";
                      }
                    }}
                  >
                    {isSelected && (
                      <div
                        style={{
                          position: "absolute",
                          top: "12px",
                          right: "12px",
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          backgroundColor: "#292D8E",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#FFFFFF",
                        }}
                      >
                        <CheckOutlined style={{ fontSize: "16px" }} />
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "64px",
                          height: "64px",
                          borderRadius: "12px",
                          backgroundColor: isSelected ? "#292D8E" : "#F5F5F5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: "16px",
                          transition: "all 0.3s ease",
                        }}
                      >
                        <img
                          src={option.icon}
                          alt={option.title}
                          style={{
                            width: "32px",
                            height: "32px",
                            filter: isSelected
                              ? "brightness(0) invert(1)"
                              : "none",
                          }}
                        />
                      </div>
                      <Typography
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          color: "#1A1A1A",
                          marginBottom: "8px",
                        }}
                      >
                        {option.title}
                      </Typography>
                      <Typography
                        style={{
                          fontSize: "13px",
                          color: "#666",
                          marginBottom: "16px",
                          minHeight: "40px",
                        }}
                      >
                        {option.description}
                      </Typography>
                      <div
                        style={{
                          width: "100%",
                          borderTop: "1px solid #E0E0E0",
                          paddingTop: "12px",
                        }}
                      >
                        {option.details.map((detail, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                              fontSize: "12px",
                              color: "#888",
                            }}
                          >
                            <div
                              style={{
                                width: "4px",
                                height: "4px",
                                borderRadius: "50%",
                                backgroundColor: isSelected
                                  ? "#292D8E"
                                  : "#999",
                                marginRight: "8px",
                              }}
                            />
                            {detail}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              marginTop: "30px !important",
              paddingTop: "20px",
              borderTop: "1px solid #E0E0E0",
            }}
          >
            <NetwalaButton
              backgroundColor={ButtonProps.background.SECONDARY}
              height={ButtonProps.height.STANDARD}
              width="120px"
              textColor={ButtonProps.color.BLACK}
              onClick={() => handleClose(false)}
              text="Cancel"
              borderColor="#D7D7D7"
            />
            <NetwalaButton
              backgroundColor={ButtonProps.background.STANDARD}
              height={ButtonProps.height.STANDARD}
              width="120px"
              textColor={ButtonProps.color.STANDARD}
              onClick={() => handleClose(true, selectedType)}
              text="Update"
              disabled={selectedType === currentType}
            />
          </div>
        </div>
      </NetwalaModal>
    </>
  );
};

export default UpdateStaffAreaLimitTypeModal;
